import React, { useState, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { vendasAPI, configSistemaAPI } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldAlert, FileSpreadsheet, Upload, Trash2, Download, Loader2, CheckCircle, AlertTriangle, Database, Undo2, UserCog } from 'lucide-react';
import { ExtensaoRemanejar } from '@/components/ExtensaoRemanejar';

const baixarBlob = (data, nome, type) => {
  const url = window.URL.createObjectURL(new Blob([data], type ? { type } : undefined));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nome);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const Seguranca = () => {
  const { user } = useAuth();
  const [totalVendas, setTotalVendas] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [arquivoImportar, setArquivoImportar] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultadoImportacao, setResultadoImportacao] = useState(null);
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState('');
  const [excluindo, setExcluindo] = useState(false);
  const fileInputRef = useRef(null);

  const carregarTotal = async () => {
    try {
      const res = await vendasAPI.list({});
      setTotalVendas(Array.isArray(res.data) ? res.data.length : 0);
    } catch {
      setTotalVendas(null);
    }
  };

  useEffect(() => { carregarTotal(); }, []);

  const handleExportarTodas = async () => {
    setExportando(true);
    try {
      const response = await vendasAPI.exportarTodas();
      baixarBlob(response.data, `todas_propostas_${new Date().getTime()}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      toast.success('Todas as propostas exportadas com sucesso!');
    } catch {
      toast.error('Erro ao exportar todas as propostas');
    } finally {
      setExportando(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await vendasAPI.downloadTemplate();
      baixarBlob(response.data, 'template_importacao_vendas.xlsx');
      toast.success('Template baixado!');
    } catch {
      toast.error('Erro ao baixar template');
    }
  };

  const handleImportar = async () => {
    if (!arquivoImportar) {
      toast.error('Selecione um arquivo');
      return;
    }
    setImportando(true);
    setResultadoImportacao(null);
    try {
      const formData = new FormData();
      formData.append('file', arquivoImportar);
      const response = await vendasAPI.importar(formData);
      setResultadoImportacao(response.data);
      if (response.data.importados > 0) {
        toast.success(`${response.data.importados} vendas importadas com sucesso!`);
        carregarTotal();
      }
    } catch (error) {
      const erro = error.response?.data?.detail || 'Erro desconhecido';
      toast.error(typeof erro === 'string' ? erro : 'Erro ao importar vendas');
      setResultadoImportacao({ sucesso: false, erro: typeof erro === 'string' ? erro : 'Erro ao importar vendas' });
    } finally {
      setImportando(false);
    }
  };

  const handleExcluirTodas = async () => {
    if (!totalVendas) {
      toast.warning('Não há vendas para excluir');
      return;
    }
    if (confirmacaoExclusao !== 'CONFIRMAR') {
      toast.warning('Digite CONFIRMAR para liberar a exclusão');
      return;
    }
    if (!window.confirm(`⚠️ ATENÇÃO: Você está prestes a EXCLUIR TODAS AS ${totalVendas} VENDAS do sistema!\n\nEsta ação NÃO pode ser desfeita.\n\nDeseja continuar?`)) return;
    setExcluindo(true);
    try {
      await vendasAPI.excluirTodas();
      toast.success(`Todas as ${totalVendas} vendas foram excluídas`);
      setConfirmacaoExclusao('');
      carregarTotal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir vendas');
    } finally {
      setExcluindo(false);
    }
  };

  // Permissões globais (flags do sistema)
  const [configSistema, setConfigSistema] = useState({ supervisor_pode_devolver: false, supervisores_podem_devolver: [], supervisores: [] });
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  useEffect(() => { configSistemaAPI.get().then((r) => setConfigSistema(r.data)).catch(() => {}); }, []);
  const salvarConfig = async (patch, msg) => {
    setSalvandoConfig(true);
    try {
      const r = await configSistemaAPI.update(patch);
      setConfigSistema((prev) => ({ ...prev, ...r.data }));
      if (msg) toast.success(msg);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSalvandoConfig(false);
    }
  };
  const alterarFlag = (chave, valor) => salvarConfig({ [chave]: valor }, valor ? 'Devolução por supervisores habilitada — selecione quem pode' : 'Devolução por supervisores desabilitada');
  const selecionados = configSistema.supervisores_podem_devolver || [];
  const toggleSupervisor = (id) => {
    const novo = selecionados.includes(id) ? selecionados.filter((s) => s !== id) : [...selecionados, id];
    salvarConfig({ supervisores_podem_devolver: novo }, null);
  };
  const selecionarTodos = (todos) => salvarConfig({ supervisores_podem_devolver: todos ? (configSistema.supervisores || []).map((s) => s.id) : [] }, todos ? 'Todos os supervisores selecionados' : 'Seleção limpa');

  if (user && user.perfil !== 'ADMIN') return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="seguranca-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <ShieldAlert className="h-8 w-8 text-red-600" />
          Segurança
        </h1>
        <p className="text-gray-500 mt-1">Operações sensíveis sobre a base de vendas — apenas administradores.</p>
      </div>

      <Card data-testid="card-permissoes">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserCog className="h-5 w-5 text-purple-500" /> Permissões</CardTitle>
          <CardDescription>Controle o que cada perfil pode fazer no fluxo de vendas.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="flex items-start gap-3">
              <Undo2 className="h-5 w-5 text-amber-500 mt-0.5" />
              <div>
                <p className="font-semibold">Supervisores podem devolver vendas para os vendedores</p>
                <p className="text-sm text-gray-500">Quando ativo, o supervisor vê o botão "Devolver ao Vendedor" nas vendas da própria equipe, informa o motivo e a venda volta como <strong>Devolvida</strong> (o vendedor é notificado e corrige). Quando inativo, só BKO/Admin devolvem.</p>
              </div>
            </div>
            <Switch
              checked={!!configSistema.supervisor_pode_devolver}
              onCheckedChange={(v) => alterarFlag('supervisor_pode_devolver', v)}
              disabled={salvandoConfig}
              data-testid="switch-supervisor-devolver"
            />
          </div>

          {configSistema.supervisor_pode_devolver && (
            <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4" data-testid="lista-supervisores-devolver">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm font-semibold">Selecione os supervisores que podem devolver vendas <span className="text-gray-500 font-normal">({selecionados.length} de {(configSistema.supervisores || []).length})</span></p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => selecionarTodos(true)} disabled={salvandoConfig} data-testid="btn-selecionar-todos-supervisores">Todos</Button>
                  <Button size="sm" variant="ghost" onClick={() => selecionarTodos(false)} disabled={salvandoConfig} data-testid="btn-limpar-supervisores">Limpar</Button>
                </div>
              </div>
              {(configSistema.supervisores || []).length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum supervisor ativo cadastrado.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {configSistema.supervisores.map((s) => {
                    const ativo = selecionados.includes(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${ativo ? 'border-amber-500 bg-amber-500/10' : 'border-gray-700/40 hover:border-amber-500/50'}`} data-testid={`supervisor-devolver-${s.id}`}>
                        <input type="checkbox" className="h-4 w-4 accent-amber-500" checked={ativo} onChange={() => toggleSupervisor(s.id)} disabled={salvandoConfig} data-testid={`check-supervisor-${s.id}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{s.nome}</p>
                          <p className="text-xs text-gray-500 truncate">{s.email}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {selecionados.length === 0 && <p className="text-xs text-amber-500 mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Nenhum supervisor selecionado — na prática ninguém consegue devolver até você marcar alguém.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="card-exportar-todas">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-gray-500" /> Exportar Todas</CardTitle>
            <CardDescription>Gera um Excel com todas as vendas do sistema, sem filtros.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-500 flex items-center gap-2"><Database className="h-4 w-4" /> Vendas no sistema: <strong data-testid="total-vendas">{totalVendas ?? '...'}</strong></p>
            <Button onClick={handleExportarTodas} disabled={exportando} className="bg-gray-700 hover:bg-gray-800 text-white" data-testid="exportar-todas-btn">
              {exportando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
              EXPORTAR TODAS
            </Button>
          </CardContent>
        </Card>

        <Card data-testid="card-importar">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-gray-500" /> Importar Vendas</CardTitle>
            <CardDescription>Importe vendas em lote a partir de um Excel no formato do template.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={handleDownloadTemplate} className="w-full" data-testid="baixar-template-btn">
              <Download className="mr-2 h-4 w-4" /> Baixar Template de Importação
            </Button>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
              <input type="file" ref={fileInputRef} accept=".xlsx,.xls" className="hidden" onChange={(e) => setArquivoImportar(e.target.files[0])} data-testid="importar-file-input" />
              {arquivoImportar ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="h-12 w-12 text-green-600 mx-auto" />
                  <p className="font-semibold text-green-700" data-testid="importar-file-name">{arquivoImportar.name}</p>
                  <Button variant="ghost" size="sm" onClick={() => { setArquivoImportar(null); setResultadoImportacao(null); }} className="text-red-600 hover:text-red-700">Remover arquivo</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="selecionar-arquivo-btn">Selecionar Arquivo</Button>
                </div>
              )}
            </div>
            {resultadoImportacao && (
              <div className={`rounded-lg p-4 text-sm ${resultadoImportacao.sucesso ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="importar-resultado">
                {resultadoImportacao.sucesso ? (
                  <>
                    <div className="flex items-center gap-2 font-semibold mb-2"><CheckCircle className="h-5 w-5" /> Importação concluída!</div>
                    <p>Importadas: <strong>{resultadoImportacao.importados}</strong> de {resultadoImportacao.total_linhas} linhas{resultadoImportacao.total_erros > 0 && ` · Erros: ${resultadoImportacao.total_erros}`}</p>
                    {resultadoImportacao.erros?.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto text-xs bg-amber-50 text-amber-700 p-2 rounded">
                        {resultadoImportacao.erros.map((erro, idx) => <p key={idx}>• {erro}</p>)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> {resultadoImportacao.erro}</div>
                )}
              </div>
            )}
            <Button onClick={handleImportar} disabled={!arquivoImportar || importando} className="bg-gray-500 hover:bg-gray-600 text-white w-full" data-testid="importar-btn">
              {importando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {importando ? 'Importando...' : 'IMPORTAR'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-red-300 dark:border-red-800" data-testid="card-excluir-todas">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600"><Trash2 className="h-5 w-5" /> Zona de Perigo — Excluir Todas as Vendas</CardTitle>
          <CardDescription>Remove permanentemente TODAS as vendas do sistema. Faça um backup (menu Backup de Vendas) antes.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 max-w-sm">
            <Label htmlFor="confirmacao-exclusao" className="text-xs text-gray-500">Digite <span className="font-mono font-bold">CONFIRMAR</span> para liberar o botão</Label>
            <Input id="confirmacao-exclusao" value={confirmacaoExclusao} onChange={(e) => setConfirmacaoExclusao(e.target.value.toUpperCase())} placeholder="CONFIRMAR" data-testid="confirmacao-exclusao-input" />
          </div>
          <Button onClick={handleExcluirTodas} disabled={excluindo || confirmacaoExclusao !== 'CONFIRMAR' || !totalVendas} className="bg-red-600 hover:bg-red-700 text-white" data-testid="excluir-todas-btn">
            {excluindo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            EXCLUIR TODAS
          </Button>
        </CardContent>
      </Card>

      <ExtensaoRemanejar />
    </div>
  );
};

export default Seguranca;
